import React, { lazy, useMemo } from "react";
import { Suspense } from "react";
import { useQuery } from "@tanstack/react-query";
import { Options } from "react-lottie";
import ContentLoader from "react-content-loader";
import useCoreContext from "@gf-core/widgets/hooks/useCoreContext";

const Lottie = lazy(() =>
  import("react-lottie").then((m) => ({
    default: m.default as unknown as React.ComponentType<{
      options: Options;
      width?: number;
      height?: number;
      speed?: number;
    }>,
  })),
);

type lottieAnimations =
  | "successAnimation"
  | "profilingAnimation"
  | "grundfosLoadingAnimation"
  | "errorAnimation";

interface LottieProps {
  animation: lottieAnimations;
  options: Omit<Options, "animationData">;
  height: number;
  width: number;
  speed?: number;
  show?: boolean;
}

const Loader = ({ width, height }: { width: number; height: number }) => {
  const { theme } = useCoreContext();

  return (
    <ContentLoader
      width={width}
      height={height}
      backgroundColor={theme.core.colors.background.layers[600]}
      foregroundColor={theme.core.colors.background.layers[500]}
    >
      <rect
        x="10"
        y="10"
        rx="5"
        ry="5"
        width={width - 20}
        height={height - 20}
      />
    </ContentLoader>
  );
};

const LazyLottie = ({
  height,
  width,
  speed,
  animation,
  options,
  show = true,
}: LottieProps) => {
  const lottieWidth = width < 0 ? 100 : width;
  const lottieHeight = height < 0 ? 100 : height;
  const { data } = useQuery({
    queryKey: [animation],
    queryFn: async () => {
      const response = await fetch(
        `https://cdn.grundfos.com/core-widgets/v1/lottie/${animation}.json`,
      );
      const data = await response.json();

      return data;
    },
    enabled: typeof window !== "undefined",
    gcTime: 1000 * 60 * 60 * 12,
  });

  const enhancedOptions = useMemo(() => {
    if (!data) return undefined;

    return {
      ...options,
      animationData: data,
    };
  }, [data, options]);

  if (!enhancedOptions)
    return <Loader height={lottieHeight} width={lottieWidth} />;

  return (
    show && (
      <Suspense fallback={<Loader height={lottieHeight} width={lottieWidth} />}>
        <Lottie
          speed={speed}
          width={lottieWidth}
          height={lottieHeight}
          options={{
            ...enhancedOptions,
          }}
        />
      </Suspense>
    )
  );
};

export default LazyLottie;
